import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  Heading,
  HStack,
  Field,
  Alert,
  Checkbox
} from '@chakra-ui/react'
import { FcGoogle } from 'react-icons/fc'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

function getBillingRedirectFromSearchParams(searchParams: URLSearchParams): string | null {
  const product = searchParams.get('product')
  const plan = searchParams.get('plan')
  const interval = searchParams.get('interval') || 'monthly'
  const source = searchParams.get('source') || 'signup'

  if (product === 'insight_pass') {
    return `/billing/checkout?plan=basic&interval=quarterly&source=${encodeURIComponent(source)}`
  }
  if (plan && (plan === 'basic' || plan === 'premium')) {
    return `/billing/checkout?plan=${encodeURIComponent(plan)}&interval=${encodeURIComponent(interval)}&source=${encodeURIComponent(source)}`
  }
  return null
}
import { useTranslation } from 'react-i18next'
import { AuthService } from '../../services/authService'
import { Logo } from '../Brand/Logo'
import { TermsOfServiceModal } from '../Legal/TermsOfServiceModal'
import { PrivacyPolicyModal } from '../Legal/PrivacyPolicyModal'
import { PasswordInput } from '../ui/PasswordInput'
import { OAuthLegalNotice } from '../Legal/OAuthLegalNotice'
import { captureReferrerFromParams } from '../../utils/referral'

interface RegisterFormData {
  displayName: string
  email: string
  password: string
  confirmPassword: string
}

interface RegisterFormErrors {
  displayName?: string
  email?: string
  password?: string
  confirmPassword?: string
  general?: string
}

export const RegisterForm: React.FC = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<RegisterFormData>({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<RegisterFormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [ageConfirmed, setAgeConfirmed] = useState(false)
  const [isToSModalOpen, setIsToSModalOpen] = useState(false)
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false)
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Pre-fill email from URL query param; capture referrer
  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam) {
      setFormData(prev => ({ ...prev, email: emailParam }))
    }
    captureReferrerFromParams(searchParams)
  }, [searchParams])

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    
    // Clear specific field error when user starts typing
    if (errors[name as keyof RegisterFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: RegisterFormErrors = {}

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required'
    } else if (formData.displayName.trim().length < 2) {
      newErrors.displayName = 'Display name must be at least 2 characters'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter'
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle Google registration
  const handleGoogleRegister = async () => {
    setIsLoading(true)
    setErrors({})

    try {
      await AuthService.signInWithGoogle()
      
      // Check for invitation token
      const token = searchParams.get('token')
      if (token) {
        navigate(`/accept-invite/${token}`, { replace: true })
        return
      }

      // Check if user came from DNA kit purchase
      const source = searchParams.get('source')
      if (source === 'dna-kit-purchase') {
        navigate('/account?tab=dna-kits', { replace: true })
        return
      }

      // Check if registration was initiated from an OAuth AI-connect flow
      const pendingOAuthId = sessionStorage.getItem('oauth_request_id')
      if (pendingOAuthId) {
        sessionStorage.removeItem('oauth_request_id')
        navigate(`/ai-connect?oauth_request_id=${encodeURIComponent(pendingOAuthId)}`, { replace: true })
        return
      }

      const billingRedirect = getBillingRedirectFromSearchParams(searchParams)
      if (billingRedirect) {
        navigate(billingRedirect, { replace: true })
        return
      }

      navigate('/redirect-to-profile', { replace: true })
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : 'Google sign-in failed',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form fields first
    if (!validateForm()) {
      return
    }
    
    // Validate required checkboxes
    if (!tosAccepted || !privacyAccepted || !ageConfirmed) {
      setErrors({
        general: 'You must confirm you are at least 18, and accept the Terms of Service and Privacy Policy to continue'
      })
      return
    }
    
    setIsLoading(true)
    // Don't clear errors here - they should be cleared when validation passes

    try {
      sessionStorage.setItem('legal_acceptance_method', 'signup')
      await AuthService.signUp(formData.email, formData.password, formData.displayName)
      
      // Check for invitation token
      const token = searchParams.get('token')
      if (token) {
        navigate(`/accept-invite/${token}`, { replace: true })
        return
      }

      // Check if user came from DNA kit purchase
      const source = searchParams.get('source')
      if (source === 'dna-kit-purchase') {
        navigate('/account?tab=dna-kits', { replace: true })
        return
      }

      // Check if registration was initiated from an OAuth AI-connect flow
      const pendingOAuthId = sessionStorage.getItem('oauth_request_id')
      if (pendingOAuthId) {
        sessionStorage.removeItem('oauth_request_id')
        navigate(`/ai-connect?oauth_request_id=${encodeURIComponent(pendingOAuthId)}`, { replace: true })
        return
      }

      const billingRedirect = getBillingRedirectFromSearchParams(searchParams)
      if (billingRedirect) {
        navigate(billingRedirect, { replace: true })
        return
      }

      navigate('/redirect-to-profile', { replace: true })
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : 'Registration failed',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Box
        w="full"
        maxW="md"
        mx="auto"
        p={8}
        bg="bg"
        borderRadius="lg"
        shadow="md"
        border="1px"
        borderColor="border"
      >
      <VStack gap={6} align="stretch">
        {/* Header */}
        <VStack gap={4}>
          <Logo size="2xl" />
          <VStack gap={2}>
            <Heading as="h2" size="md" color="green.600" _dark={{ color: 'green.400' }} textAlign="center">
              Create Account
            </Heading>
            <Text color="fg.muted" textAlign="center">
              Sign up to get started
            </Text>
          </VStack>
        </VStack>

        {/* Error Alert */}
        {errors.general && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            <Alert.Content>
              {errors.general}
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Google Registration Button */}
        <Button
          onClick={handleGoogleRegister}
          loading={isLoading}
          variant="outline"
          borderColor="border"
          _hover={{ bg: 'bg.muted' }}
          size="lg"
          disabled={isLoading}
        >
          <HStack gap={2}>
            <FcGoogle />
            <Text>Continue with Google</Text>
          </HStack>
        </Button>

        <OAuthLegalNotice />

        <HStack align="center" w="full">
          <Box flex="1" h="1px" bg="border" />
          <Text color="fg.muted" fontSize="sm" px={3}>
            or
          </Text>
          <Box flex="1" h="1px" bg="border" />
        </HStack>

        {/* Registration Form */}
        <Box as="form" onSubmit={handleEmailRegister}>
          <VStack gap={4}>
            <Field.Root invalid={!!errors.displayName} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                Display Name
              </Field.Label>
              <Input
                name="displayName"
                type="text"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="Enter your display name"
                bg="bg"
                color="fg"
                borderColor={errors.displayName ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="name"
              />
              <Field.ErrorText>{errors.displayName}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors.email} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                Email
              </Field.Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                bg="bg"
                color="fg"
                borderColor={errors.email ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="email"
              />
              <Field.ErrorText>{errors.email}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors.password} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                Password
              </Field.Label>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                bg="bg"
                color="fg"
                borderColor={errors.password ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="new-password"
              />
              <Field.ErrorText>{errors.password}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!errors.confirmPassword} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                Confirm Password
              </Field.Label>
              <PasswordInput
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                bg="bg"
                color="fg"
                borderColor={errors.confirmPassword ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="new-password"
              />
              <Field.ErrorText>{errors.confirmPassword}</Field.ErrorText>
            </Field.Root>

            {/* Terms of Service and Privacy Policy Checkboxes */}
            <VStack gap={3} align="stretch" w="full">
              <Checkbox.Root
                checked={ageConfirmed}
                onCheckedChange={(details) => setAgeConfirmed(details.checked === true)}
                colorPalette="green"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label fontSize="sm" color="fg">
                  I confirm I am at least 18 years old
                </Checkbox.Label>
              </Checkbox.Root>

              <Checkbox.Root
                checked={tosAccepted}
                onCheckedChange={(details) => setTosAccepted(details.checked === true)}
                colorPalette="green"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label fontSize="sm" color="fg">
                  I agree to the{' '}
                  <Text
                    as="span"
                    color="green.600"
                    _dark={{ color: 'green.400' }}
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsToSModalOpen(true)
                    }}
                  >
                    Terms of Service
                  </Text>
                </Checkbox.Label>
              </Checkbox.Root>
              
              <Checkbox.Root
                checked={privacyAccepted}
                onCheckedChange={(details) => setPrivacyAccepted(details.checked === true)}
                colorPalette="green"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label fontSize="sm" color="fg">
                  I agree to the{' '}
                  <Text
                    as="span"
                    color="green.600"
                    _dark={{ color: 'green.400' }}
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsPrivacyModalOpen(true)
                    }}
                  >
                    Privacy Policy
                  </Text>
                  , including processing of genetic data if I upload it
                </Checkbox.Label>
              </Checkbox.Root>
            </VStack>

            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              w="full"
              loading={isLoading}
              disabled={isLoading}
            >
              Create Account
            </Button>
          </VStack>
        </Box>

        {/* Links */}
        <Text fontSize="sm" color="fg.muted" textAlign="center">
          Already have an account?{' '}
          <RouterLink to={`/login${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}>
            <Text
              as="span"
              color="green.600"
              _dark={{ color: 'green.400' }}
              fontWeight="medium"
              _hover={{ textDecoration: 'underline' }}
            >
              Sign In
            </Text>
          </RouterLink>
        </Text>
      </VStack>
    </Box>
    
    {/* Terms of Service Modal */}
    <TermsOfServiceModal
      isOpen={isToSModalOpen}
      onClose={() => setIsToSModalOpen(false)}
    />
    
    {/* Privacy Policy Modal */}
    <PrivacyPolicyModal
      isOpen={isPrivacyModalOpen}
      onClose={() => setIsPrivacyModalOpen(false)}
    />
  </>
  )
}

export default RegisterForm